import React, {PropsWithChildren, useEffect} from 'react';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {API, MAX_RETRIES_REQUESTS_NUMBER, PAYLOAD_FIELDS_NAMES} from '@chekin/api';
import {
  FormField,
  GuestSchemaV2,
  AgreementClause,
  DocumentChoice,
  IDENTITY_VERIFICATION_TYPES,
  QUERY_CACHE_KEYS,
} from '@chekin/core';
import {useChekinSDK} from '../ChekinSDK';
import {useSummary} from '../summary';
import {useCustomTheme} from '../customTheme';
import {toastResponseError} from 'utils/common';

const StaleTime = 60_000;
const CacheTime = 60_000;

type IdentityVerificationDetails = {
  enabled: boolean;
  required: boolean;
  ocr: boolean;
  biomatch: boolean;
  documents: DocumentChoice[];
  isIvExempted: boolean;
};

export type AddFormType = {
  [key: string]: FormField;
};

export type ContextProps = {
  identityVerificationDetails: IdentityVerificationDetails;
  isGuestSchemaLoaded: boolean;
  isGuestSchemaLoading: boolean;
  isGuestSchemaFetching: boolean;
  isGuestSchemaInitialLoading: boolean;
  formFields: AddFormType;
  agreementClauses?: AgreementClause[];
  reservationDocumentsList?: DocumentChoice[];
  setBirthCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setArrivalCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setArrivalDistrict?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNextDestinationCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setNationality?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDocumentType?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setDocumentExpeditionCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setResidenceCountry?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setResidenceProvince?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setCitizenship?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setBirthDate?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setHasPhone?: React.Dispatch<React.SetStateAction<boolean>>;
  setIsLeaderGuest?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setInvoiceLeaderType?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setRecipientCodeType?: React.Dispatch<React.SetStateAction<string | undefined>>;
};

const GuestContext = React.createContext<ContextProps>({
  formFields: {},
  isGuestSchemaLoaded: false,
  isGuestSchemaLoading: false,
  isGuestSchemaFetching: false,
  isGuestSchemaInitialLoading: false,
  identityVerificationDetails: {
    enabled: false,
    required: false,
    ocr: false,
    biomatch: false,
    documents: [],
    isIvExempted: false,
  },
});

function GuestProvider({children}: PropsWithChildren) {
  const {isSuccessSDK} = useChekinSDK();
  const {isTemplateInitialized} = useCustomTheme();
  const {reservationId, isSuccess: isSuccessSummary} = useSummary();
  const [isGuestSchemaLoaded, setIsGuestSchemaLoaded] = React.useState(false);
  const [birthCountry, setBirthCountry] = React.useState<string>();
  const [nationality, setNationality] = React.useState<string>();
  const [documentType, setDocumentType] = React.useState<string>();
  const {t, i18n} = useTranslation();
  const [documentExpeditionCountry, setDocumentExpeditionCountry] =
    React.useState<string>();
  const [arrivalCountry, setArrivalCountry] = React.useState<string>();
  const [arrivalDistrict, setArrivalDistrict] = React.useState<string>();
  const [nextDestinationCountry, setNextDestinationCountry] = React.useState<string>();
  const [residenceCountry, setResidenceCountry] = React.useState<string>();
  const [residenceProvince, setResidenceProvince] = React.useState<string>();
  const [birthDate, setBirthDate] = React.useState<string>();
  const [hasPhone, setHasPhone] = React.useState<boolean>(false);
  const [citizenship, setCitizenship] = React.useState<string>();
  const [isLeaderGuest, setIsLeaderGuest] = React.useState<boolean | undefined>(
    undefined,
  );
  const [invoiceLeaderType, setInvoiceLeaderType] = React.useState<string>();
  const [recipientCodeType, setRecipientCodeType] = React.useState<string>();

  const {
    data: guestSchema,
    error: guestSchemaError,
    isLoading: isGuestSchemaLoading,
    isFetching: isGuestSchemaFetching,
  } = useQuery<GuestSchemaV2>({
    queryKey: [
      QUERY_CACHE_KEYS.formFields,
      nationality,
      citizenship,
      birthCountry,
      arrivalCountry,
      arrivalDistrict,
      residenceCountry,
      residenceProvince,
      nextDestinationCountry,
      documentExpeditionCountry,
      documentType,
      birthDate,
      hasPhone,
      isLeaderGuest,
      i18n.language,
      invoiceLeaderType,
      recipientCodeType,
      reservationId,
    ],
    queryFn: () =>
      API.guest.getSchema({
        [PAYLOAD_FIELDS_NAMES.reservation_id]: reservationId as string,
        [PAYLOAD_FIELDS_NAMES.birth_place_country]: birthCountry,
        [PAYLOAD_FIELDS_NAMES.nationality]: nationality,
        [PAYLOAD_FIELDS_NAMES.document_expedition_country]: documentExpeditionCountry,
        [PAYLOAD_FIELDS_NAMES.next_destination_country]: nextDestinationCountry,
        [PAYLOAD_FIELDS_NAMES.arrived_from_country]: arrivalCountry,
        [PAYLOAD_FIELDS_NAMES.arrived_from_district]: arrivalDistrict,
        [PAYLOAD_FIELDS_NAMES.residence_country]: residenceCountry,
        [PAYLOAD_FIELDS_NAMES.residence_province]: residenceProvince,
        [PAYLOAD_FIELDS_NAMES.document_type]: documentType,
        [PAYLOAD_FIELDS_NAMES.citizenship]: citizenship,
        [PAYLOAD_FIELDS_NAMES.birth_date]: birthDate,
        [PAYLOAD_FIELDS_NAMES.phone]: hasPhone,
        [PAYLOAD_FIELDS_NAMES.is_leader]: isLeaderGuest,
        [PAYLOAD_FIELDS_NAMES.force_show_leaders_fields]: isLeaderGuest,
        [PAYLOAD_FIELDS_NAMES.invoice_leader_type]: invoiceLeaderType,
        [PAYLOAD_FIELDS_NAMES.recipient_code_type]: recipientCodeType,
      }),
    enabled: Boolean(isSuccessSDK) && Boolean(isSuccessSummary) && isTemplateInitialized,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: StaleTime,
    gcTime: CacheTime,
    retry: MAX_RETRIES_REQUESTS_NUMBER,
    placeholderData: keepPreviousData,
  });

  const isGuestSchemaInitialLoading = isGuestSchemaLoading && !isGuestSchemaLoaded;

  useEffect(() => {
    if ((guestSchema || guestSchemaError) && !isGuestSchemaLoaded) {
      setIsGuestSchemaLoaded(true);
    }
  }, [guestSchema, guestSchemaError, isGuestSchemaLoaded]);

  useEffect(() => {
    if (guestSchemaError) {
      toastResponseError(
        'Something went wrong while fetching guest fields schema. If the error persists, contact our support team.',
      );
    }
  }, [guestSchemaError]);

  const formFieldsData = guestSchema?.guest_fields;
  const agreementClauses = guestSchema?.agreement_clauses;

  const reservationDocumentsList = React.useMemo(() => {
    return guestSchema?.iv?.document_choices.map(dc => ({
      ...dc,
      label: t(`${dc.label}`),
    }));
  }, [t, guestSchema?.iv?.document_choices]);

  const identityVerificationDetails = React.useMemo<IdentityVerificationDetails>(() => {
    const ivDetails = guestSchema?.iv;
    const isOCR =
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.ocr ||
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.document;
    const isBiomatch =
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.biomatch;
    const isIvExempted = Boolean(ivDetails?.is_iv_exempted);

    return {
      enabled: (isOCR || isBiomatch) && !isIvExempted,
      required: Boolean(ivDetails?.required),
      documents: ivDetails?.document_choices || [],
      biomatch: isBiomatch,
      ocr: isOCR,
      isIvExempted,
    };
  }, [guestSchema?.iv]);

  const transformFormFieldsArrayToObject = React.useCallback((): AddFormType => {
    if (!formFieldsData) return {};
    return formFieldsData?.reduce((accumulator, field) => {
      return {...accumulator, [field.name]: field};
    }, {});
  }, [formFieldsData]);

  const formFields = React.useMemo<AddFormType>(
    () => transformFormFieldsArrayToObject(),
    [transformFormFieldsArrayToObject],
  );

  const defaultProps = React.useMemo<ContextProps>(
    () => ({
      formFields,
      identityVerificationDetails,
      setBirthCountry,
      setArrivalCountry,
      setArrivalDistrict,
      setNextDestinationCountry,
      setNationality,
      setDocumentType,
      setDocumentExpeditionCountry,
      setResidenceCountry,
      setResidenceProvince,
      setCitizenship,
      setBirthDate,
      setHasPhone,
      setIsLeaderGuest,
      setInvoiceLeaderType,
      setRecipientCodeType,
      isGuestSchemaLoaded,
      isGuestSchemaLoading,
      isGuestSchemaFetching,
      isGuestSchemaInitialLoading,
      reservationDocumentsList,
      agreementClauses,
    }),
    [
      formFields,
      identityVerificationDetails,
      isGuestSchemaLoaded,
      isGuestSchemaLoading,
      isGuestSchemaFetching,
      isGuestSchemaInitialLoading,
      reservationDocumentsList,
      agreementClauses,
    ],
  );

  return <GuestContext.Provider value={defaultProps} children={children} />;
}

export {GuestContext, GuestProvider};
